/**
 * ProDecorPackagesCard — Decoration Team professional builds up to 10 simple
 * decor packages. Each package = image + name + price + (Wedding ▾|Other).
 *
 * Customer side: these packages render under the pro's Decoration Team card
 * in Instant Pricing and open a lightbox slideshow when clicked.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Image as ImgIcon, Trash, Plus, FloppyDisk, Warning, UploadSimple,
  VideoCamera, Play, Sparkle, Spinner,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PACKAGES = 10;
const MAX_MEDIA_IMAGES = 5;
const MAX_MEDIA_VIDEOS = 1;

const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

const blankPkg = () => ({
  id: `local-${Math.random().toString(36).slice(2, 10)}`,
  name: '',
  price: '',
  image: '',
  event_kind: 'wedding',
  media: [],
});

const ProDecorPackagesCard = () => {
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  // Track which package is currently uploading media — separate from cover-image uploads.
  const [uploadingMedia, setUploadingMedia] = useState({});
  const fileRefs = useRef({});
  const mediaImgRefs = useRef({});
  const mediaVidRefs = useRef({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/decor-packages`, { withCredentials: true });
      const list = (data.packages || []).map((p) => ({
        id: p.id,
        name: p.name || '',
        price: p.price ? String(p.price) : '',
        image: p.image || '',
        event_kind: p.event_kind === 'other' ? 'other' : 'wedding',
        media: Array.isArray(p.media) ? p.media : [],
      }));
      setPackages(list);
    } catch { /* not a decoration_team pro — keep empty */ }
  };

  const update = (idx, patch) => {
    setPackages((cur) => cur.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addPackage = () => {
    if (packages.length >= MAX_PACKAGES) {
      toast.error(`Maximum ${MAX_PACKAGES} packages`);
      return;
    }
    setPackages((cur) => [...cur, blankPkg()]);
  };

  const removePackage = (idx) => {
    setPackages((cur) => cur.filter((_, i) => i !== idx));
  };

  const onPickImage = (idx) => {
    fileRefs.current[idx]?.click();
  };

  const onImageChange = async (idx, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setUploadingIdx(idx);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/me/decor-packages/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update(idx, { image: data.url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploadingIdx(null);
    if (fileRefs.current[idx]) fileRefs.current[idx].value = '';
  };

  // ---- Per-package media (5 photos + 1 ≤30s video, AI-moderated) ----

  const uploadMedia = async (idx, file) => {
    const pkg = packages[idx];
    if (!pkg || pkg.id?.startsWith('local-')) {
      toast.info('Save the package first, then add photos/videos to it.');
      return;
    }
    if (!file) return;
    if (file.type.startsWith('video/') && file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50 MB');
      return;
    }
    if (file.type.startsWith('image/') && file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadingMedia((m) => ({ ...m, [idx]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/me/decor-packages/${encodeURIComponent(pkg.id)}/upload-media`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      update(idx, { media: [...(pkg.media || []), data.item] });
      toast.success(`${data.item.kind === 'video' ? 'Video' : 'Photo'} added to package`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploadingMedia((m) => ({ ...m, [idx]: false }));
  };

  const onMediaImageChange = async (idx, e) => {
    const f = e.target.files?.[0];
    await uploadMedia(idx, f);
    if (mediaImgRefs.current[idx]) mediaImgRefs.current[idx].value = '';
  };

  const onMediaVideoChange = async (idx, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side duration pre-check.
    try {
      const url = URL.createObjectURL(f);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;
      const dur = await new Promise((res, rej) => {
        v.onloadedmetadata = () => res(v.duration);
        v.onerror = () => rej(new Error('bad video'));
        setTimeout(() => rej(new Error('timeout')), 8000);
      }).catch(() => 0);
      URL.revokeObjectURL(url);
      if (dur > 0 && dur > 30.5) {
        toast.error(`Video is ${dur.toFixed(1)}s — must be ≤ 30 s.`);
        if (mediaVidRefs.current[idx]) mediaVidRefs.current[idx].value = '';
        return;
      }
    } catch {
      // Browser couldn't probe — let server reject if needed.
    }
    await uploadMedia(idx, f);
    if (mediaVidRefs.current[idx]) mediaVidRefs.current[idx].value = '';
  };

  const removeMedia = async (idx, mediaId) => {
    const pkg = packages[idx];
    if (!pkg || pkg.id?.startsWith('local-')) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/me/decor-packages/${encodeURIComponent(pkg.id)}/media/${encodeURIComponent(mediaId)}`,
        { withCredentials: true },
      );
      update(idx, { media: (pkg.media || []).filter((m) => m.id !== mediaId) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  const save = async () => {
    const payload = packages
      .map((p) => ({
        id: p.id?.startsWith('local-') ? undefined : p.id,
        name: (p.name || '').trim(),
        price: p.price === '' ? null : Number(p.price),
        image: (p.image || '').trim(),
        event_kind: p.event_kind === 'other' ? 'other' : 'wedding',
        media: Array.isArray(p.media) ? p.media : [],
      }))
      .filter((p) => p.name);
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/me/decor-packages`,
        { packages: payload }, { withCredentials: true });
      toast.success(`Saved ${data.count} package${data.count === 1 ? '' : 's'}`);
      // Re-load to get back any server-assigned ids.
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const validCount = packages.filter((p) => (p.name || '').trim()).length;

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="pro-decor-packages-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImgIcon size={20} className="text-brand-primary" weight="fill" /> Decor Packages
        </CardTitle>
        <CardDescription>
          Build up to <span className="font-semibold">{MAX_PACKAGES}</span> simple decor packages — each is a single
          photo, a price, and a tag for either <span className="font-semibold">Wedding-tier events</span>{' '}
          or <span className="font-semibold">Other events</span>. Customers see all your packages under your name
          on Instant Pricing and can open a slideshow.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {packages.map((p, idx) => (
            <div key={p.id} className="border border-[#E8E1D5] rounded p-3 space-y-2" data-testid={`decor-package-${idx}`}>
              {/* Image */}
              <div className="aspect-[4/3] bg-sand-100 border border-[#E8E1D5] flex items-center justify-center overflow-hidden cursor-pointer relative group"
                onClick={() => onPickImage(idx)}
                data-testid={`decor-package-image-${idx}`}>
                {p.image ? (
                  <img src={p.image.startsWith('http') ? p.image : `${BACKEND_URL}${p.image}`}
                    alt={p.name || `Package ${idx + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-text-secondary">
                    <UploadSimple size={28} weight="duotone" className="mx-auto" />
                    <p className="text-[10px] mt-1 uppercase font-bold tracking-wider">Tap to upload</p>
                  </div>
                )}
                {uploadingIdx === idx && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-sand-50 text-xs uppercase tracking-wider font-bold">
                    Uploading…
                  </div>
                )}
                {p.image && uploadingIdx !== idx && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="text-sand-50 text-[10px] uppercase font-bold tracking-wider bg-black/60 px-2 py-1">Replace</span>
                  </div>
                )}
              </div>
              <input ref={(el) => { fileRefs.current[idx] = el; }} type="file"
                accept="image/jpeg,image/png,image/webp" onChange={(e) => onImageChange(idx, e)}
                className="hidden" data-testid={`decor-package-file-${idx}`} />

              {/* Name */}
              <Input value={p.name} onChange={(e) => update(idx, { name: e.target.value })}
                placeholder="Package name (e.g. Pastel Floral Mandap)"
                className="h-9 rounded-none border-[#E8E1D5]"
                data-testid={`decor-package-name-${idx}`} />

              {/* Price + Event-kind selector */}
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-1">
                  <span className="text-xs text-text-secondary">₹</span>
                  <Input type="number" min={0} value={p.price}
                    onChange={(e) => update(idx, { price: e.target.value })}
                    placeholder="Price"
                    className="h-9 rounded-none border-[#E8E1D5] text-right text-xs"
                    data-testid={`decor-package-price-${idx}`} />
                </div>
                <select value={p.event_kind}
                  onChange={(e) => update(idx, { event_kind: e.target.value })}
                  className="h-9 px-2 text-xs font-bold border border-[#E8E1D5] rounded-none bg-white"
                  data-testid={`decor-package-event-${idx}`}>
                  <option value="wedding">Wedding tier</option>
                  <option value="other">Other events</option>
                </select>
                <button type="button" onClick={() => removePackage(idx)}
                  className="h-9 w-9 border border-red-200 text-red-500 hover:bg-red-50 rounded-none flex items-center justify-center"
                  title="Delete package"
                  data-testid={`decor-package-delete-${idx}`}>
                  <Trash size={14} />
                </button>
              </div>

              {/* Per-package mini-gallery — 5 photos + 1 ≤30s video, AI-moderated.
                  Only available once the package has been saved (so we have a server id). */}
              {p.id?.startsWith('local-') ? (
                <p className="text-[10px] text-text-secondary italic mt-1">
                  Save the package to start adding social photos / videos.
                </p>
              ) : (
                <div className="space-y-1.5 pt-2 border-t border-[#E8E1D5]">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] uppercase tracking-wider font-bold text-text-secondary">
                      Social media (no captions)
                    </span>
                    <span className="text-[9px] text-text-secondary">
                      {(p.media || []).filter((m) => m.kind === 'image').length}/{MAX_MEDIA_IMAGES} pics ·{' '}
                      {(p.media || []).filter((m) => m.kind === 'video').length}/{MAX_MEDIA_VIDEOS} vid
                    </span>
                  </div>

                  {/* Thumbnails */}
                  {(p.media || []).length > 0 && (
                    <div className="grid grid-cols-4 gap-1">
                      {p.media.map((m) => (
                        <div key={m.id}
                          className="relative aspect-square border border-[#E8E1D5] bg-black overflow-hidden group"
                          data-testid={`decor-package-media-${idx}-${m.id}`}>
                          {m.kind === 'video' ? (
                            <>
                              {m.poster ? (
                                <img src={fullUrl(m.poster)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <video src={fullUrl(m.url)} className="w-full h-full object-cover" preload="metadata" muted />
                              )}
                              <Play size={14} weight="fill" className="absolute inset-0 m-auto text-white drop-shadow" />
                            </>
                          ) : (
                            <img src={fullUrl(m.url)} alt="" className="w-full h-full object-cover" />
                          )}
                          <button type="button" onClick={() => removeMedia(idx, m.id)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-white/90 text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
                            data-testid={`decor-package-media-delete-${idx}-${m.id}`}>
                            <Trash size={9} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Photo / Add Video buttons */}
                  <div className="flex items-center gap-1.5">
                    <input ref={(el) => { mediaImgRefs.current[idx] = el; }} type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => onMediaImageChange(idx, e)}
                      className="hidden" data-testid={`decor-package-media-img-input-${idx}`} />
                    <Button size="sm" variant="outline"
                      disabled={uploadingMedia[idx] || (p.media || []).filter((m) => m.kind === 'image').length >= MAX_MEDIA_IMAGES}
                      onClick={() => mediaImgRefs.current[idx]?.click()}
                      className="flex-1 rounded-none border-dashed border-[#E8E1D5] text-[10px] h-7 disabled:opacity-40"
                      data-testid={`decor-package-add-photo-${idx}`}>
                      {uploadingMedia[idx] ? <Spinner size={10} className="mr-1 animate-spin" /> : <UploadSimple size={10} className="mr-1" />}
                      Add Photo
                    </Button>
                    <input ref={(el) => { mediaVidRefs.current[idx] = el; }} type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      onChange={(e) => onMediaVideoChange(idx, e)}
                      className="hidden" data-testid={`decor-package-media-vid-input-${idx}`} />
                    <Button size="sm" variant="outline"
                      disabled={uploadingMedia[idx] || (p.media || []).filter((m) => m.kind === 'video').length >= MAX_MEDIA_VIDEOS}
                      onClick={() => mediaVidRefs.current[idx]?.click()}
                      className="flex-1 rounded-none border-dashed border-[#E8E1D5] text-[10px] h-7 disabled:opacity-40"
                      data-testid={`decor-package-add-video-${idx}`}>
                      {uploadingMedia[idx] ? <Spinner size={10} className="mr-1 animate-spin" /> : <VideoCamera size={10} className="mr-1" />}
                      Add Video
                    </Button>
                  </div>
                  {uploadingMedia[idx] && (
                    <div className="flex items-center gap-1 text-[9px] text-amber-700 italic">
                      <Sparkle size={9} weight="fill" /> AI is checking for caption text…
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {packages.length < MAX_PACKAGES && (
          <Button variant="outline" onClick={addPackage}
            className="rounded-none border-dashed border-[#E8E1D5] w-full mt-3 text-xs h-9"
            data-testid="decor-package-add-btn">
            <Plus size={14} className="mr-1" /> Add decor package ({packages.length}/{MAX_PACKAGES})
          </Button>
        )}

        {validCount === 0 && (
          <p className="mt-3 text-[11px] text-amber-700 italic flex items-center gap-1">
            <Warning size={12} /> Add at least one package — customers can't see your decor lineup until then.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-xs text-text-secondary">
            {validCount} package{validCount === 1 ? '' : 's'} ready to save
          </p>
          <Button onClick={save} disabled={saving}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
            data-testid="decor-package-save-btn">
            <FloppyDisk size={16} className="mr-2" weight="fill" />
            {saving ? 'Saving…' : 'Save Packages'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProDecorPackagesCard;
